// Basic calculator for amount fields, e.g. "250+150+100+350" or "2*(120+30)-15/3".
// Follows BODMAS: brackets first, then × and ÷, then + and −. No eval().

const OPERATORS = /[+\-*/×÷x()]/i;

// Normalise what people type: ×, x, ÷, commas (1,200), spaces, "−" (unicode minus)
const normalise = (text) => String(text ?? '')
  .replace(/,/g, '')
  .replace(/\s+/g, '')
  .replace(/[×xX]/g, '*')
  .replace(/÷/g, '/')
  .replace(/[−–]/g, '-');

const tokenize = (text) => {
  const tokens = [];
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    if ('+-*/()'.includes(ch)) {
      tokens.push(ch);
      i += 1;
    } else if (/[0-9.]/.test(ch)) {
      let j = i;
      while (j < text.length && /[0-9.]/.test(text[j])) j += 1;
      const raw = text.slice(i, j);
      if ((raw.match(/\./g) || []).length > 1 || raw === '.') return null;
      tokens.push(parseFloat(raw));
      i = j;
    } else {
      return null;
    }
  }
  return tokens;
};

// Recursive descent: expression = term (('+'|'-') term)*, term = factor (('*'|'/') factor)*
const parse = (tokens) => {
  let pos = 0;
  const peek = () => tokens[pos];

  const factor = () => {
    const token = tokens[pos++];
    if (token === '-') { const v = factor(); return v === null ? null : -v; }
    if (token === '+') return factor();
    if (token === '(') {
      const v = expression();
      if (tokens[pos++] !== ')') return null;
      return v;
    }
    return typeof token === 'number' ? token : null;
  };

  const term = () => {
    let v = factor();
    while (v !== null && (peek() === '*' || peek() === '/')) {
      const op = tokens[pos++];
      const rhs = factor();
      if (rhs === null || (op === '/' && rhs === 0)) return null;
      v = op === '*' ? v * rhs : v / rhs;
    }
    return v;
  };

  const expression = () => {
    let v = term();
    while (v !== null && (peek() === '+' || peek() === '-')) {
      const op = tokens[pos++];
      const rhs = term();
      if (rhs === null) return null;
      v = op === '+' ? v + rhs : v - rhs;
    }
    return v;
  };

  const result = expression();
  return pos === tokens.length && result !== null && isFinite(result) ? result : null;
};

// Value of a plain number or calculation, rounded to paise; null if it can't be worked out
export const evaluateAmount = (text) => {
  const clean = normalise(text);
  if (!clean) return null;
  const tokens = tokenize(clean);
  if (!tokens || tokens.length === 0) return null;
  const value = parse(tokens);
  return value === null ? null : Math.round(value * 100) / 100;
};

// True when the text is a calculation rather than a single number ("-5" alone is not)
export const isAmountExpression = (text) => {
  const clean = normalise(text);
  return clean.length > 1 && OPERATORS.test(clean.slice(1));
};

// Tidy form of the calculation for saving in notes, e.g. "250 + 150 × 2"
export const formatAmountExpression = (text) => normalise(text)
  .replace(/\*/g, ' × ')
  .replace(/\//g, ' ÷ ')
  .replace(/(\d|\))([+-])/g, '$1 $2 ')
  .replace(/\s+/g, ' ')
  .trim();
